/* eslint-disable no-unused-expressions */
import { faHandPointer } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CancelIcon from '@mui/icons-material/Cancel';
import { Chip } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { orange } from '@mui/material/colors';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import { BrSelect } from '@govbr-ds/react-components';
import { makeStyles } from '@mui/styles';
import { readOnlyModeAtom } from 'contexts/FeatureContext';
import OptionsBuilder from 'dominios/OptionsBuilder';
import { useAtom } from 'jotai';
import { useBuildInputId, useKskForm } from 'ksk/styledComponents/components/KskForm/KskForm';
import { get, isEmpty, isFunction } from 'lodash';
import { useCallback, useEffect, useState, useRef } from 'react';
import { Controller } from 'react-hook-form';
import { ensuredForwardRef } from 'react-use';
import { arrayToMap, doNothing, stopEvent } from 'utils/utils';
import KskInputHelper from '../KskInputHelper/KskInputHelper';
import KskPlaceHolder from '../KskPlaceHolder/KskPlaceHolder';

const useStyles = makeStyles((theme) => ({
  indeterminateColor: {
    color: orange[800]
  },
  selectAllText: {
    fontWeight: 500
  },
  multipleSelect: {
    '&& .MuiSelect-select': {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: '0.5em',
      height: 'auto',
      whiteSpace: 'normal',
      overflow: 'visible',
      textOverflow: 'clip'
    },
    '&& .MuiSelect-select .MuiChip-root.MuiChip-root': {
      margin: 0,
      maxWidth: '100%',
      height: 'auto',
      minHeight: 24
    },
    '&& .MuiSelect-select .MuiChip-label': {
      whiteSpace: 'normal',
      overflowWrap: 'anywhere'
    }
  }
}));

const KskSelect = ensuredForwardRef(({
  label = 'LABEL PLACEHOLDER',
  withLabel = true,
  id,
  name,
  required,
  optional,
  controlClassName = '',
  onChange,
  className,
  children,
  control,
  form,
  tooltip,
  dominio,
  fetchData,
  dependencies = [],
  disabled,
  loading = false,
  readOnly,
  options,
  valueAsObject,
  onMap,
  compareWith = 'codigo',
  displayEmpty = true,
  showEmptyValue = false,
  multiple,
  reset,
  helperText,
  errors,
  hidden,
  style,
  defaultValue,
  renderElement,
  disableReadOnlyContext = false,
  hideDropdownIcon = false,

  ...otherProps
}, ref) => {

  const classes = useStyles();
  const errorsObj = form?.formState?.errors || errors;
  const error = get(errorsObj, name);
  const _isSubmitted = form?.formState?.isSubmitted ?? true;
  const hasErrors = _isSubmitted && !isEmpty(error);
  const errorText = error?.message;

  const _id = useBuildInputId({ id, name });
  const { formRef } = useKskForm() || {};
  const labelId = `label-${_id}`;
  const _control = control || form?.control;
  const _onChange = onChange ?? doNothing;
  const {
    IconComponent: otherIconComponent,
    ...selectOtherProps
  } = otherProps;

  const selectIconProps = hideDropdownIcon
    ? { IconComponent: () => null }
    : loading
      ? { IconComponent: (props) => <CircularProgress {...props} size={16} /> }
      : (otherIconComponent ? { IconComponent: otherIconComponent } : {});

  const [readOnlyMode] = useAtom(readOnlyModeAtom);
  const _readOnly = (!disableReadOnlyContext) && (readOnly || readOnlyMode);

  const labelWithIcon = (
    <>
      { !(disabled || _readOnly) && <FontAwesomeIcon icon={faHandPointer}/> }
      <span> {label}</span>
    </>
  );
  const selectLabel = withLabel ? labelWithIcon : label;

  const [store, setStore] = useState(null);
  const [optionList, setOptionList] = useState(null);
  const fetchDataCallback = useCallback(fetchData, dependencies);

  const [open, setOpen] = useState(false);
  const ignoreFocusRef = useRef(false);

  const handleOpen = () => setOpen(true);
  const handleClose = useCallback(() => {
    ignoreFocusRef.current = true;
    setOpen(false);
    setTimeout(() => { ignoreFocusRef.current = false; }, 100);
  }, []);

  useEffect(() => {
    if (!open) return;

    const formElement = formRef?.current;
    formElement?.addEventListener('submit', handleClose);
    return () => formElement?.removeEventListener('submit', handleClose);
  }, [formRef, open, handleClose]);

  const handleFocus = (e) => {
    if (!ignoreFocusRef.current && !(disabled || _readOnly)) {
      setOpen(true);
    }
    if (otherProps.onFocus) {
      otherProps.onFocus(e);
    }
  };

  const getSelected = () => {
    const value = form?.watch(name);
    if (multiple) {
      return valueAsObject ? value?.map(v => v?.codigo) ?? [] : value ?? [];
    }
    return valueAsObject ? value?.codigo ?? '' : value ?? '';
  };

  const createStore = useCallback((list) => {
    multiple && setOptionList(list);

    const mappedOptions = arrayToMap({
      array: list,
      mapFn: o => ({ key: o.codigo, value: o })
    });

    setStore({
      options: OptionsBuilder({
        dominio: list,
        multiple,
        selected: getSelected(),
        classes,
        valueAsObject,
        emptyOption: displayEmpty
      }),
      values: valueAsObject ? list : list.map(o => o.codigo),
      getOption: (key) => mappedOptions.get(key)
    });

    isFunction(onMap) && onMap(list.map(({ codigo, nome }) => ({ codigo, nome })));
  }, [displayEmpty, valueAsObject]);

  useEffect(() => {
    if (store && multiple) {
      store.options = OptionsBuilder({
        dominio: optionList,
        multiple,
        selected: getSelected(),
        classes,
        valueAsObject,
        emptyOption: displayEmpty
      });
      setStore({ ...store });
    }
  }, [JSON.stringify(getSelected())]);

  useEffect(() => {
    if (fetchDataCallback) {
      fetchDataCallback().then(r => createStore(r?.data));
    }
  }, []);

  useEffect(() => { options && createStore(options); }, [options]);
  useEffect(() => { dominio && createStore(dominio); }, [dominio]);

  const emitOnChange = (value) => {
    const syntheticEvent = {
      target: {
        name,
        value
      }
    };

    _onChange(syntheticEvent);
  };

  const setValueAndReset = (val) => {
    form?.setValue(name, val);
    emitOnChange(val);
  };

  const toggleAll = () => {
    const all = store.values;
    setValueAndReset(
      getSelected().length === all.length ? [] : [...all]
    );
  };

  const setValue = (e, forced) => {
    const value = e?.target?.value ?? forced;
    if (multiple && value[value.length - 1] === '__ALL__') {
      toggleAll();
      return;
    }
    if (valueAsObject) {
      setValueAndReset(
        multiple ? value.map(v => store.getOption(v)) : store.getOption(value)
      );
    } else {
      setValueAndReset(value);
    }
  };

  const onChipDelete = (e, v) => {
    stopEvent(e);
    setValue(null, getSelected().filter(x => x !== v));
  };

  if (!store) {
    return <KskPlaceHolder className={className} height="2.5em" text={label} />;
  }

  const renderValueProp = multiple ? {
    renderValue: (selected) =>
      selected.map(v => (
        <Chip
          key={v}
          size="small"
          label={store.getOption(v)?.nome}
          color="primary"
          onDelete={disabled || _readOnly ? undefined : e => onChipDelete(e, v)}
          deleteIcon={<CancelIcon onMouseDown={stopEvent} />}
        />
      ))
  } : renderElement ? {
    renderValue: v => renderElement(store.getOption(v))
  } : {};

  const hiddenStyle = hidden ? {display: 'none'} : {};
  const _style = {...(style || {}), ...hiddenStyle };

  const formControl = (
    <FormControl className={className} error={!!errorText} style={_style} >
      {withLabel && <InputLabel id={labelId} disabled={disabled || _readOnly}>{labelWithIcon}</InputLabel>}

      <Controller
        name={name}
        control={_control}
        defaultValue={defaultValue}
        render={({ field }) => (
          <Select
            ref={ref}
            id={_id}
            labelId={labelId}
            label={selectLabel}
            variant="outlined"
            className={`${controlClassName} ${multiple ? classes.multipleSelect : ''}`.trim()}
            multiple={multiple}
            displayEmpty={showEmptyValue}
            value={getSelected()}
            disabled={disabled || _readOnly}
            onChange={setValue}
            open={open}
            onOpen={handleOpen}
            onClose={handleClose}
            onFocus={handleFocus}
            {...selectIconProps}
            MenuProps={{
              autoFocus: true,
              ...(selectOtherProps.MenuProps || {}),
              MenuListProps: {
                autoFocusItem: true,
                ...(selectOtherProps.MenuProps?.MenuListProps || {}),
                onKeyDownCapture: (e) => {
                  if (e.key === 'Tab') {
                    e.stopPropagation();
                    handleClose();
                    
                    const selectEl = document.getElementById(_id);
                    if (selectEl) {
                      selectEl.focus();
                    }
                  }
                  if (selectOtherProps.MenuProps?.MenuListProps?.onKeyDownCapture) {
                    selectOtherProps.MenuProps.MenuListProps.onKeyDownCapture(e);
                  }
                }
              }
            }}
            {...renderValueProp}
            {...selectOtherProps}
          >
            {store.options}
            {children}
          </Select>
        )}
      />

      <FormHelperText>
        <KskInputHelper {...{ optional, required, hasErrors, name, errorsObj, helperText, readOnly: disabled || _readOnly }} />
      </FormHelperText>
    </FormControl>
  );

  return formControl;
});

export default KskSelect;
